import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Badge,
  IconButton,
  TextField,
  Button,
  Chip,
  CircularProgress,
  Alert,
  InputAdornment,
} from '@mui/material';
import {
  Search as SearchIcon,
  Send as SendIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Delete as DeleteIcon,
  Forum as ConversationsIcon,
  Group as GroupIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { axiosService } from 'shared/helpers/axios';
import { useSearchParams } from 'react-router-dom';
import { useUser } from "shared/helpers/axios";
import PageContainer from 'components/common/layout/PageContainer';
import PageHeader from 'components/common/layout/PageHeader';
import { useTheme } from '@mui/material/styles';
import { useUserStatus, getStatusMeta } from 'shared/contexts/UserStatusContext';

const MessagesPage = () => {
  const [searchParams] = useSearchParams();
  const recipientId = searchParams.get('user');
  const consortiumId = searchParams.get('consortium');
  const senderActorId = searchParams.get('sender');
  const recipientActorId = searchParams.get('recipient');
  const theme = useTheme();

  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messageBody, setMessageBody] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  
  const messagesEndRef = useRef(null);
  const { user: currentUser } = useUser();
  const { status: myStatus } = useUserStatus();
  const myStatusMeta = getStatusMeta(myStatus);

  // Scroll to bottom of messages container (not the whole page)
  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Fetch conversations on mount
  useEffect(() => {
    fetchConversations();
    fetchUnreadCount();
    
    // Dispatch event to notify AppBar to refresh message count
    window.dispatchEvent(new CustomEvent('messagesPageOpened'));
    
    // Set up polling for new messages every 30 seconds
    const interval = setInterval(() => {
      if (selectedConversation) {
        refreshCurrentConversation();
      }
      fetchUnreadCount();
    }, 30000);
    
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // If recipientId, consortiumId, or actor IDs are provided, open the corresponding conversation
  useEffect(() => {
    const hasTarget = recipientId || consortiumId || (senderActorId && recipientActorId);
    if (conversations.length > 0 && currentUser) {
      if (senderActorId && recipientActorId) {
        handleActorIds(senderActorId, recipientActorId);
      } else if (recipientId) {
        handleRecipientId(recipientId);
      } else if (consortiumId) {
        handleConsortiumId(consortiumId);
      }
    } else if (hasTarget && !currentUser) {
      // Wait for currentUser to load
      const timer = setTimeout(() => {
        if (currentUser) {
          if (senderActorId && recipientActorId) handleActorIds(senderActorId, recipientActorId);
          else if (recipientId) handleRecipientId(recipientId);
          else if (consortiumId) handleConsortiumId(consortiumId);
        }
      }, 1000);
      return () => clearTimeout(timer);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recipientId, consortiumId, senderActorId, recipientActorId, conversations, currentUser]);

  const handleActorIds = async (senderId, recipientId) => {
    // Find conversation matching the exact sender+recipient actor pair
    const existingConv = conversations.find(conv => {
      return (
        (conv.sender_actor?.id === senderId && conv.recipient_actor?.id === recipientId) ||
        (conv.sender_actor?.id === recipientId && conv.recipient_actor?.id === senderId)
      );
    });
    
    if (existingConv) {
      await handleSelectConversation(existingConv);
    }
  };

  const handleRecipientId = async (userId) => {
    // Check if conversation exists
    const existingConv = conversations.find(conv => {
      // For user-to-user conversations
      if (conv.conversation_type === 'user_to_user') {
        const otherActor = conv.sender_actor.entity_id === currentUser?.id 
          ? conv.recipient_actor 
          : conv.sender_actor;
        return otherActor.entity_id === userId;
      }
      return false;
    });
    
    if (existingConv) {
      await handleSelectConversation(existingConv);
    } else {
      // Start new conversation
      await fetchRecipientInfo(userId);
    }
  };

  const handleConsortiumId = async (entityId) => {
    // Find the consortium conversation
    const existingConv = conversations.find(conv => {
      if (conv.conversation_type === 'user_to_consortium') {
        const consortiumActor = (conv.recipient_actor?.actor_type === 'consortium' || conv.recipient_actor?.actor_type === 'organization')
          ? conv.recipient_actor
          : conv.sender_actor;
        return consortiumActor.entity_id === entityId;
      }
      return false;
    });
    
    if (existingConv) {
      await handleSelectConversation(existingConv);
    }
  };

  const fetchConversations = async () => {
    setLoading(true);
    try {
      const response = await axiosService.get('/interact/messages/conversations/');
      setConversations(response.data);
      setError(null);
    } catch (err) {
      setError('Failed to load conversations');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchUnreadCount = async () => {
    try {
      const response = await axiosService.get('/interact/messages/unread-count/');
      setUnreadCount(response.data.unread_count);
      
      // Notify AppBar to refresh its count
      window.dispatchEvent(new CustomEvent('messagesPageOpened'));
    } catch (err) {
      console.error('Failed to fetch unread count:', err);
    }
  };

  const fetchRecipientInfo = async (userId) => {
    setLoadingMessages(true);
    try {
      const response = await axiosService.get(`/users/${userId}/`);
      const user = response.data;
      
      // Create a temporary conversation object for new conversation
      const tempConversation = {
        id: `temp-${userId}`,
        conversation_type: 'user_to_user',
        sender_actor: {
          id: currentUser?.id,
          actor_type: 'user',
          entity_id: currentUser?.id,
          display_name: currentUser?.name || `${currentUser?.first_name} ${currentUser?.last_name}`,
          avatar_url: currentUser?.avatar,
        },
        recipient_actor: {
          id: user.id,
          actor_type: user.actor_type || 'user',
          entity_id: user.id,
          display_name: user.name || `${user.first_name} ${user.last_name}`,
          avatar_url: user.avatar,
        },
        unread_count: 0,
        last_message: null,
      };
      
      setSelectedConversation(tempConversation);
      setMessages([]);
    } catch (err) {
      setError('Failed to load recipient information');
      console.error(err);
    } finally {
      setLoadingMessages(false);
    }
  };

  const refreshCurrentConversation = async () => {
    if (!selectedConversation) return;
    
    try {
      let params = {};
      
      if (selectedConversation.conversation_type === 'user_to_user') {
        const otherUserActor = selectedConversation.sender_actor?.entity_id === currentUser?.id 
          ? selectedConversation.recipient_actor 
          : selectedConversation.sender_actor;
        
        params = {
          partner_type: 'user',
          partner_id: otherUserActor?.entity_id
        };
      } else if (selectedConversation.conversation_type === 'user_to_consortium') {
        const consortiumActor = selectedConversation.recipient_actor?.actor_type === 'consortium' 
          ? selectedConversation.recipient_actor 
          : selectedConversation.sender_actor;
        
        params = {
          partner_type: 'consortium',
          partner_id: consortiumActor?.entity_id,
          sender_actor_id: selectedConversation.sender_actor?.id,
          recipient_actor_id: selectedConversation.recipient_actor?.id
        };
      }
      
      const response = await axiosService.get('/interact/messages/', { params });
      
      // Sort messages by date (oldest first)
      const sortedMessages = (response.data.results || response.data).sort(
        (a, b) => new Date(a.created) - new Date(b.created)
      );
      
      setMessages(sortedMessages);
    } catch (err) {
      console.error('Failed to refresh messages:', err);
    }
  };

  const handleSelectConversation = async (conversation, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    setSelectedConversation(conversation);
    setLoadingMessages(true);


    try {
      let params = {};
      
      if (conversation.conversation_type === 'user_to_user') {
        const otherUserActor = conversation.sender_actor?.entity_id === currentUser?.id 
          ? conversation.recipient_actor 
          : conversation.sender_actor;
        
        params = {
          partner_type: 'user',
          partner_id: otherUserActor?.entity_id
        };
      } else if (conversation.conversation_type === 'user_to_consortium') {
        const consortiumActor = conversation.recipient_actor?.actor_type === 'consortium' 
          ? conversation.recipient_actor 
          : conversation.sender_actor;
        
        params = {
          partner_type: 'consortium',
          partner_id: consortiumActor?.entity_id,
          sender_actor_id: conversation.sender_actor?.id,
          recipient_actor_id: conversation.recipient_actor?.id
        };
      }
      
      const response = await axiosService.get('/interact/messages/', { params });
      
      // Sort messages by date (oldest first)
      const sortedMessages = (response.data.results || response.data).sort(
        (a, b) => new Date(a.created) - new Date(b.created)
      );
      setMessages(sortedMessages);
      
      // Mark conversation as read if there are unread messages
      if (conversation.unread_count > 0) {
        await axiosService.post('/interact/messages/mark-conversation-read/', {
          partner_type: params.partner_type,
          partner_id: params.partner_id
        });
        
        // Update unread count in conversation list
        setConversations(prev => prev.map(conv => {
          if (conv === conversation) {
            return { ...conv, unread_count: 0 };
          }
          return conv;
        }));
        
        fetchUnreadCount();
      }
      
      setError(null);
    } catch (err) {
      setError('Failed to load messages');
      console.error(err);
    } finally {
      setLoadingMessages(false);
    }
  };

  const handleSendMessage = async () => {
    if (!messageBody.trim() || !selectedConversation || !currentUser) return;

    setSending(true);
    try {
      let senderType, senderEntityId, recipientType, recipientEntityId;
      
      if (selectedConversation.conversation_type === 'user_to_consortium') {
        // In consortium conversations, determine who is sending based on conversation context
        const consortiumActor = selectedConversation.recipient_actor?.actor_type === 'consortium'
          ? selectedConversation.recipient_actor
          : selectedConversation.sender_actor;
        const userActor = selectedConversation.recipient_actor?.actor_type === 'consortium'
          ? selectedConversation.sender_actor
          : selectedConversation.recipient_actor;
        
        // If the user in this conversation is NOT me, then I'm a coordinator viewing their thread
        // and should reply as the consortium. Otherwise, I'm the user sending to consortium.
        if (userActor.entity_id !== currentUser.id) {
          // Coordinator replying: send as consortium to the user
          senderType = 'consortium';
          senderEntityId = consortiumActor.entity_id;
          recipientType = 'user';
          recipientEntityId = userActor.entity_id;
        } else {
          // Regular user: send as myself to consortium
          senderType = 'user';
          senderEntityId = currentUser.id;
          recipientType = 'consortium';
          recipientEntityId = consortiumActor.entity_id;
        }
      } else {
        // User-to-user conversation
        const otherUserActor = selectedConversation.sender_actor?.entity_id === currentUser?.id
          ? selectedConversation.recipient_actor
          : selectedConversation.sender_actor;
        
        senderType = 'user';
        senderEntityId = currentUser?.id;
        recipientType = 'user';
        recipientEntityId = otherUserActor?.entity_id;
      }

      // This matches your MessageCreateSerializer
      const response = await axiosService.post('/interact/messages/', {
        sender_actor_type: senderType,
        sender_entity_id: senderEntityId,
        recipient_actor_type: recipientType,
        recipient_entity_id: recipientEntityId,
        body: messageBody.trim(),
        // Optional fields
        subject: '',  // Can be empty as per your serializer
        priority: 'normal'  // Default value
      });

      // Add new message to the list - response.data should match MessageSerializer format
      setMessages(prevMessages => [...prevMessages, response.data]);
      setMessageBody('');
      
      // Refresh conversations to update last message
      fetchConversations();
      setError(null);
    } catch (err) {
      setError('Failed to send message');
      console.error('Send message error:', err.response?.data || err);
      setError(err.response?.data?.detail || err.response?.data?.non_field_errors?.[0] || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleToggleStar = async (messageId) => {

    try {
      // This matches your toggle-star endpoint
      await axiosService.post(`/interact/messages/${messageId}/toggle-star/`);
      
      // Update local state based on who is starring
      setMessages(prev => prev.map(msg => {
        if (msg.id === messageId) {
          const isCurrentUserSender = msg.sender_actor?.entity_id === currentUser?.id;
          
          if (isCurrentUserSender) {
            return { ...msg, is_starred_by_sender: !msg.is_starred_by_sender };
          } else {
            return { ...msg, is_starred_by_recipient: !msg.is_starred_by_recipient };
          }
        }
        return msg;
      }));
    } catch (err) {
      console.error('Failed to toggle star:', err);
    }
  };

  const handleDeleteMessage = async (messageId) => {
    if (!window.confirm('Are you sure you want to delete this message?')) return;

    try {
      // This matches your destroy method (soft delete)
      await axiosService.delete(`/interact/messages/${messageId}/`);
      setMessages(prev => prev.filter(msg => msg.id !== messageId));
      fetchConversations();
    } catch (err) {
      setError('Failed to delete message');
      console.error(err);
    }
  };

  const filteredConversations = conversations.filter(conv => {
    if (!searchQuery) return true;
    
    const searchTerm = searchQuery.toLowerCase();
    
    if (conv.conversation_type === 'user_to_consortium') {
      const consortiumActor = conv.recipient_actor?.actor_type === 'consortium' 
        ? conv.recipient_actor 
        : conv.sender_actor;
      const otherActor = conv.sender_actor?.actor_type === 'consortium' 
        ? conv.recipient_actor 
        : conv.sender_actor;
      
      return (otherActor?.display_name?.toLowerCase().includes(searchTerm) ||
              consortiumActor?.display_name?.toLowerCase().includes(searchTerm));
    } else {
      const otherActor = conv.sender_actor?.entity_id === currentUser?.id 
        ? conv.recipient_actor 
        : conv.sender_actor;
      
      return otherActor?.display_name?.toLowerCase().includes(searchTerm);
    }
  });

  const formatMessageTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = (now - date) / (1000 * 60 * 60);
    
    if (diffInHours < 24) {
      return format(date, 'h:mm a');
    } else if (diffInHours < 48) {
      return 'Yesterday';
    } else {
      return format(date, 'MMM d');
    }
  };

  const renderConversationList = () => (
    <Paper sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden'}}>
      {/* Current user status strip */}
      <Box sx={{ px: 2, py: 1, borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1.25, bgcolor: `${myStatusMeta.color}08` }}>
        <Badge
          overlap="circular"
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          badgeContent=""
          // sx={{ '& .MuiBadge-badge': { minWidth: 9, width: 9, height: 9, borderRadius: '50%', 
          //   bgcolor: myStatusMeta.color, border: '1.5px solid white', p: 0 } }}
        >
          <Avatar
            src={currentUser?.avatar || undefined}
            sx={{ width: 28, height: 28, fontSize: '0.75rem' }}
          >
            {currentUser?.first_name?.[0] || currentUser?.username?.[0] || '?'}
          </Avatar>
        </Badge>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.primary', display: 'block', lineHeight: 1.3 }}>
            {currentUser?.first_name ? `${currentUser.first_name} ${currentUser.last_name || ''}`.trim() : currentUser?.username || 'You'}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: myStatusMeta.color, flexShrink: 0 }} />
            <Typography variant="caption" sx={{ color: myStatusMeta.color, fontWeight: 600, fontSize: '0.68rem' }}>{myStatusMeta.label}</Typography>
            {/* <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.75rem' }}>· {myStatusMeta.desc}</Typography> */}
          </Box>
        </Box>
      </Box>
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search conversations..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <List sx={{ flexGrow: 1, overflow: 'auto' }}>
        {loading && conversations.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : filteredConversations.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              No conversations yet
            </Typography>
          </Box>
        ) : (
          filteredConversations.map((conv) => {
            // Generate a unique key using actor IDs
            const convKey = `${conv.sender_actor?.id}_${conv.recipient_actor?.id}`;
            
            let displayName = '';
            let displayAvatar = null;
            let avatarContent = null;
            
            if (conv.conversation_type === 'user_to_consortium') {
              const otherActor = conv.sender_actor?.actor_type === 'consortium' 
                ? conv.recipient_actor 
                : conv.sender_actor;
              const consortiumActor = conv.recipient_actor?.actor_type === 'consortium' 
                ? conv.recipient_actor 
                : conv.sender_actor;
              
              displayName = `${otherActor?.display_name || 'User'} → ${consortiumActor?.display_name || 'Consortium'}`;
              displayAvatar = otherActor?.avatar_url;
              avatarContent = <GroupIcon />;
            } else {
              const otherActor = conv.sender_actor?.entity_id === currentUser?.id
                ? conv.recipient_actor
                : conv.sender_actor;
              
              displayName = otherActor?.display_name || 'User';
              displayAvatar = otherActor?.avatar_url;
              avatarContent = displayName.charAt(0).toUpperCase();
            }
            
            
            
            return (
              <ListItem key={convKey} disablePadding >
                <ListItemButton
                  selected={selectedConversation?.thread_id === conv.thread_id}
                  onClick={(event) => handleSelectConversation(conv, event)}
                  sx={{ py: 1.5 }}
                >
                  <ListItemAvatar>
                    <Badge 
                      badgeContent={conv.unread_count} 
                      color="primary"
                      invisible={conv.unread_count === 0}
                    >
                      <Avatar 
                        src={displayAvatar}
                        alt={displayName || 'Conversation'}
                        sx={{
                          bgcolor: theme.palette.primary.avatar2,
                          color: theme.palette.primary.contrastText,
                        }}
                      >
                        {avatarContent}
                      </Avatar>
                    </Badge>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography 
                          variant="subtitle2" 
                          sx={{ 
                            fontWeight: conv.unread_count > 0 ? 600 : 400,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            maxWidth: '150px'
                          }}
                        >
                          {displayName}
                        </Typography>
                        {conv.last_message?.created && (
                          <Typography variant="caption" color="text.secondary">
                            {formatMessageTime(conv.last_message.created)}
                          </Typography>
                        )}
                      </Box>
                    }
                    secondary={
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          fontWeight: conv.unread_count > 0 ? 500 : 400,
                          maxWidth: '200px'
                        }}
                      >
                        {conv.last_message?.is_from_me && 'You: '}
                        {conv.last_message?.body_preview || 'No messages yet'}
                      </Typography>
                    }
                  />
                </ListItemButton>
              </ListItem>
            );
          })
        )}
      </List>
    </Paper>
  );

  const renderMessageThread = () => {
    if (!selectedConversation) {
      return (
        <Paper sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Box sx={{ textAlign: 'center', p: 3 }}>
            <ConversationsIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Select a conversation
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Choose a conversation from the list to view messages
            </Typography>
          </Box>
        </Paper>
      );
    }

    // Determine header information
    let headerActor = null;
    let headerTitle = '';
    let headerSubtitle = '';
    
    if (selectedConversation.conversation_type === 'user_to_consortium') {
      const consortiumActor = selectedConversation.recipient_actor?.actor_type === 'consortium'
        ? selectedConversation.recipient_actor
        : selectedConversation.sender_actor;
      const otherActor = selectedConversation.sender_actor?.actor_type === 'consortium'
        ? selectedConversation.recipient_actor
        : selectedConversation.sender_actor;
      
      headerActor = otherActor;
      headerTitle = otherActor?.display_name || 'User';
      headerSubtitle = `Conversation with ${consortiumActor?.display_name || 'Consortium'}`;
    } else {
      headerActor = selectedConversation.sender_actor?.entity_id === currentUser?.id
        ? selectedConversation.recipient_actor
        : selectedConversation.sender_actor;
      headerTitle = headerActor?.display_name || 'User';
      headerSubtitle = headerActor?.position || null;
    }

    return (
      <Paper sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Header */}
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar 
            src={headerActor?.avatar_url}
            alt={headerTitle}
            sx={{
              bgcolor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
              width: 48,
              height: 48
            }}
          >
            {headerTitle.charAt(0).toUpperCase()}
          </Avatar>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h6">
              {headerTitle}
            </Typography>
            {headerSubtitle && (
              <Typography variant="caption" color="text.secondary">
                {headerSubtitle}
              </Typography>
            )}
          </Box>
        </Box>

        {/* Messages */}
        <Box sx={{ flex: 1, overflowY: 'auto', p: 2, display: 'flex', flexDirection: 'column', }}>
          {loadingMessages ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : messages.length === 0 ? (
            <Box sx={{ textAlign: 'center', p: 3 }}>
              <Typography variant="body2" color="text.secondary">
                No messages yet. Start the conversation!
              </Typography>
            </Box>
          ) : (
            messages.map((message, index) => {

              // Determine if message is "mine" (appears on right in purple)
              let isMine;
              
              if (selectedConversation?.conversation_type === 'user_to_consortium') {
                // For consortium conversations, check if I'm viewing someone else's thread or my own
                const conversationUserActor = selectedConversation.recipient_actor?.actor_type === 'consortium'
                  ? selectedConversation.sender_actor
                  : selectedConversation.recipient_actor;
                
                // If conversation user is NOT me, I'm a coordinator viewing their thread
                if (conversationUserActor.entity_id !== currentUser?.id) {
                  // Coordinator view: messages FROM consortium are mine
                  isMine = message.sender_actor?.actor_type === 'consortium' || 
                           message.sender_actor?.actor_type === 'organization';
                } else {
                  // Regular user view: messages FROM me are mine
                  isMine = message.sender_actor?.entity_id === currentUser?.id;
                }
              } else {
                // User-to-user conversation
                isMine = message.sender_actor?.entity_id === currentUser?.id;
              }
              
              const showAvatar = index === 0 || 
                messages[index - 1]?.sender_actor?.id !== message.sender_actor?.id;
              
              return (
                <Box
                  key={message.id}
                  sx={{
                    mb: 2,
                    display: 'flex',
                    flexDirection: isMine ? 'row-reverse' : 'row',
                    alignItems: 'flex-start',
                    gap: 1,
                  }}
                >
                  {showAvatar ? (
                    <Avatar
                      src={message.sender_actor?.avatar_url}
                      alt={message.sender_actor?.display_name || 'User'}
                      sx={{ 
                        width: 36, 
                        height: 36,
                        bgcolor: theme.palette.primary.main,
                        color: theme.palette.primary.contrastText,
                      }}
                    >
                      {message.sender_actor?.actor_type === 'consortium' ? (
                        <GroupIcon fontSize="small" />
                      ) : (
                        (message.sender_actor?.display_name || 'U').charAt(0).toUpperCase()
                      )}
                    </Avatar>
                  ) : (
                    <Box sx={{ width: 36 }} /> // Spacer for alignment
                  )}
                  
                  <Box sx={{ maxWidth: '70%' }}>
                    {!isMine && showAvatar && message.sender_actor?.display_name && (
                      <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5, ml: 1 }}>
                        {message.sender_actor.display_name}
                      </Typography>
                    )}
                    
                    <Paper
                      elevation={0}
                      sx={{
                        bgcolor: isMine ? 'primary.main' : 'grey.100',
                        color: isMine ? 'white' : 'text.primary',
                        borderRadius: 2,
                        p: 1.5,
                        wordBreak: 'break-word'
                      }}
                    >
                      <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                        {message.body}
                      </Typography>
                      
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                        <Typography variant="caption" sx={{ opacity: 0.7 }}>
                          {format(new Date(message.created), 'h:mm a')}
                        </Typography>
                        
                        <Box>
                          <IconButton
                            size="small"
                            onClick={() => handleToggleStar(message.id)}
                            sx={{ color: isMine ? 'white' : 'inherit' }}
                          >
                            {(isMine ? message.is_starred_by_sender : message.is_starred_by_recipient) ? (
                              <StarIcon fontSize="small" />
                            ) : (
                              <StarBorderIcon fontSize="small" />
                            )}
                          </IconButton>
                          
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteMessage(message.id)}
                            sx={{ color: isMine ? 'white' : 'inherit' }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </Box>
                    </Paper>
                  </Box>
                </Box>
              );
            })
          )}
          <div ref={messagesEndRef} />
        </Box>

        {/* Compose */}
        <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
            <TextField
              fullWidth
              multiline
              maxRows={4}
              placeholder="Type your message..."
              value={messageBody}
              onChange={(e) => setMessageBody(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && e.ctrlKey) {
                  e.preventDefault();
                  handleSendMessage();
                }
              }}
              size="small"
            />
            <Button
              variant="contained"
              onClick={handleSendMessage}
              disabled={!messageBody.trim() || sending}
              startIcon={sending ? <CircularProgress size={16} /> : <SendIcon />}
              sx={{ minWidth: 100 }}
            >
              Send
            </Button>
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            Press Ctrl+Enter to send
          </Typography>
        </Box>
      </Paper>
    );
  };

  return (
    <PageContainer sx={{ height: 'calc(100vh - 80px)', pb: 2 }}>
      <PageHeader title="Messages" subtitle="Send and receive direct messages with other users">
        {unreadCount > 0 && <Chip label={`${unreadCount} unread`} color="primary" size="small" />}
      </PageHeader>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={2} sx={{ height: 'calc(100% - 92px)' }}>
        <Grid item xs={12} md={4} sx={{ height: '100%' }}>
          {renderConversationList()}
        </Grid>
        <Grid item xs={12} md={8} sx={{ height: '100%' }}>
          {renderMessageThread()}
        </Grid>
      </Grid>
    </PageContainer>
  );
};

export default MessagesPage;